import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import { FiltersToolbar } from "@components/commerce/product-listing/filters/toolbar/FiltersToolbar";
import { ProductHitsWrapper } from "@components/commerce/product-listing/product-tile-list/ProductHitsWrapper";
import ProductListFooter from "@components/commerce/product/list/product-list-footer/ProductListFooter";
import { EccoLoading } from "@components/ui/ecco-loading/EccoLoading";
import type { Document } from "@contentful/rich-text-types";
import { useNormalizedInfiniteHits } from "@hooks/useNormalizedInfiniteHits";
import { AnalyticsListTypes } from "@lib/analytics/analyticsTypes";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { H } from "@ui/components/content/heading/A11yHeading";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { sizes } from "@ui/design-tokens";
import type { FC } from "react";
import { useContext } from "react";
import { useInstantSearch } from "react-instantsearch";
import SearchCMS from "./cms-search";

interface SearchResultsPLPProps {
    authorizationInvitation: Document;
    listOfFilters: any;
    routerQuery: any;
    locale: string;
}

export const SearchResultsPLP: FC<SearchResultsPLPProps> = ({
    authorizationInvitation,
    listOfFilters,
    routerQuery,
    locale,
}) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { filtersDialogOpened } = useContext(FiltersContext);
    const { hits, resultQuery, numberOfHits } = useNormalizedInfiniteHits();
    const { status } = useInstantSearch();

    const query = resultQuery || routerQuery.query;
    const isAlgoliaLoading = status === "loading" || status === "stalled";

    return (
        <>
            {numberOfHits !== 0 && (
                <H size="h1" color="gray.500" mt={[4, 6]} mb={[10, 12]} ml={[3, 3, 4, 6]}>
                    {getMicrocopy(CommonCMS.page, SearchCMS.searchInputWithQueryHeadline)}
                    <span style={{ color: "black", display: "inline" }}>
                        {" "}
                        &ldquo;{`${query}`}&rdquo;
                    </span>{" "}
                    <Box
                        fontSize={{
                            base: "mobileH3",
                            lg: "desktopH3",
                        }}
                        fontWeight="bold"
                        color="black"
                        display="inline-flex"
                    >
                        ({numberOfHits})
                    </Box>
                </H>
            )}
            <main data-testid="SearchResultsData">
                <Box
                    sx={{
                        position: "sticky",
                        w: ["100%", "fit-content"],
                        top: [
                            `calc(${sizes.navBarCollapsedHeight} + 25px)`,
                            `calc(${sizes.navBarCollapsedHeight} + 25px)`,
                            `calc(${sizes.navBarCollapsedHeight} + 35px)`,
                        ],
                        zIndex: filtersDialogOpened ? 3 : 2,
                        boxSizing: "border-box",
                        display: numberOfHits === 0 && "none",
                        px: [
                            "layoutMargin.mobile",
                            "layoutMargin.tab",
                            "layoutMargin.desktop",
                            "layoutMargin.desktop",
                        ],
                    }}
                >
                    <FiltersToolbar listOfFilters={listOfFilters} locale={locale} />
                </Box>
                {query === undefined || (isAlgoliaLoading && hits.length === 0) ? (
                    <EccoLoading />
                ) : (
                    <ProductHitsWrapper
                        productListHits={hits}
                        footerContent={<ProductListFooter />}
                        noResultsContent={<EmptyResultsHeadline query={query as string} />}
                        productListName={AnalyticsListTypes.search}
                        authorizationInvitation={authorizationInvitation}
                    />
                )}
            </main>
        </>
    );
};

interface IEmptyResultsHeadlineProps {
    query: string;
}

const EmptyResultsHeadline: FC<IEmptyResultsHeadlineProps> = ({ query }) => {
    const { get: getMicrocopy } = useMicrocopy();

    return (
        <Flex direction={"column"} my={12}>
            <H
                as="h1"
                fontSize={["mobileHeading1", "mobileHeading1", "desktopHeading1"]}
                color="gray.500"
            >
                {getMicrocopy(CommonCMS.page, SearchCMS.noResultsSearchText)}
            </H>
            <H size="h1" fontSize={["mobileHeading1", "mobileHeading1", "desktopHeading1"]}>
                {getMicrocopy(CommonCMS.page, SearchCMS.noResultsSearchTextQuery, {
                    replacements: { query },
                })}
            </H>
        </Flex>
    );
};
